import { html, on } from './dom';

export function createElement(
    tagName: string,
    attributes?: { [key: string]: string },
    innerContent?: string,
): HTMLElement {
    const element = document.createElement(tagName);
    if (attributes) {
        for (const [key, value] of Object.entries(attributes)) {
            element.setAttribute(key, value);
        }
    }
    if (innerContent) element.textContent = innerContent;

    return element;
}

export function generateLoadingSkeleton(lines: number = 5): HTMLDivElement {
    const skeletonLines = Array.from({ length: lines }, () => {
        const width = Math.floor(Math.random() * 40) + 60;

        return html<HTMLSpanElement>`
            <span aria-live="polite" aria-busy="true" class="esc-skeleton">
                <span style="width: ${width}%">&#8204;</span>
            </span>
        `;
    });

    return html<HTMLDivElement>` <div class="esc-skeleton-container">${skeletonLines}</div> `;
}

export function isExperienceEditor(href: string): boolean {
    return /sc_mode=(edit|preview)/.test(href);
}

export function isExperienceEditorEditing(href: string): boolean {
    return /sc_mode=edit/.test(href);
}

export function sgwLinkButtonIcon(
    label: string = 'Knapp',
    icon: string = 'external-link',
    pos: string = 'left',
    size: number = 24,
    onClick: ((event: MouseEvent) => void) | undefined = undefined,
): HTMLButtonElement {
    const button = html<HTMLButtonElement>`
        <button
            class="sgw-button sgw-button-common button--link__icon-${pos} icon-${pos} button--link button--size-standard"
            type="button"
        >
            <span class="sgw-link__text sgw-link__text--icon-visible icon-${pos}">
                <span>${label}</span>
                <span class="sgw-icon" aria-hidden="true">
                    <span class="icon icon--22 icon--${icon}-${size} icon--${size}"></span>
                </span>
            </span>
        </button>
    `;

    if (onClick) {
        on(button, 'click', (event) => onClick(event as MouseEvent));
    }

    return button;
}

export function piwikFormatName(input: string): string {
    if (!input || input.trim() === '') {
        return '';
    }

    return input
        .trim()
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ä/g, 'a')
        .replace(/ö/g, 'o')
        .replace(/ø/g, 'o')
        .replace(/å/g, 'a')
        .replace(/ß/g, 'ss')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * LLM tokenizers often omit the leading space before number tokens.
 * This function detects when a space should be inserted between streamed
 * tokens and returns the corrected text.
 *
 * Rules (evaluated on the LAST CHARACTER of lastToken):
 *  - No lastToken                                  → no change
 *  - text already starts with whitespace                → no change
 *  - number-starting token: keep existing compact number rules
 *  - letter-starting token after sentence punctuation (.!?) → insert a space
 */
export function ensureLLMNumberSpacing(text: string, lastToken: string | null, currentBuffer: string = '') {
    if (!lastToken) {
        return text;
    }

    // Step 2: Already has leading whitespace — nothing to do.
    if (/^\s/.test(text)) {
        return text;
    }

    // Step 2b: If we're still inside an opening HTML tag (no closing '>')
    // keep streamed tag names/attributes compact (e.g. "<h" + "2" => "<h2").
    if (currentBuffer) {
        const lastOpenAngle = currentBuffer.lastIndexOf('<');
        const lastCloseAngle = currentBuffer.lastIndexOf('>');
        if (lastOpenAngle > lastCloseAngle) {
            return text;
        }
    }

    // Step 3: Examine only the last character of the previous token.
    // That's the character that directly borders the new token.
    const lastChar = lastToken[lastToken.length - 1];
    const startsWithDigit = /^\d/.test(text);
    const startsWithLetter = /^[A-Za-zÆØÅæøå]/.test(text);

    // Step 4: Previous token already ends with whitespace — no gap needed.
    if (/\s/.test(lastChar)) {
        return text;
    }

    // Step 4b: Sentence punctuation + next word token.
    // Example: "Ferdig." + "Neste" => "Ferdig. Neste"
    if (startsWithLetter && /[.!?]/.test(lastChar)) {
        return ` ${text}`;
    }

    // Non-number tokens do not need extra handling beyond sentence punctuation.
    if (!startsWithDigit) {
        return text;
    }

    // Step 5: Previous token ends with a digit → we're mid-number (e.g. "123" + "456").
    // Do NOT insert a space here.
    if (/\d/.test(lastChar)) {
        return text;
    }

    // Step 6: Previous token ends with a character that naturally precedes a number
    // without a space: minus sign, open paren, open bracket, decimal point.
    // Examples: "-42", "(10", "[0", ".5"
    if (/[-.([{]/.test(lastChar)) {
        return text;
    }

    // Step 6b: Decimal comma in streamed tokens (e.g. "5" + "," + "29").
    // Keep number formatting compact when the char before the comma is a digit.
    if (lastChar === ',') {
        const charBeforeComma = currentBuffer.length > 1 ? currentBuffer[currentBuffer.length - 2] : '';
        if (/\d/.test(charBeforeComma)) {
            return text;
        }
    }

    // Step 7: Everything else — letters, underscores, colons, semicolons,
    // closing brackets, commas, etc. — warrants a space before the number.
    return ` ${text}`;
}
